import { useEffect, useState } from 'react'
import type { AccessSync } from '../api/types'
import type { Api } from '../api'
import { ageWords, dayTime, listWords } from './accessWords'

/**
 * THE STALE-DATA STRIP (05 section 7.2): for System Admins, on every page, under the
 * new model, while MBI's copy of Mindzer is older than the server's limit (two
 * missed hourly updates). Access keeps following the last good update meanwhile,
 * so nothing else on a report screen would say so.
 *
 * Read when it mounts, every ten minutes, and when the browser tab comes back into
 * view. A failed read keeps the last answer: a reminder that vanished on one
 * dropped request would say "fresh" when nothing said so.
 */
const EVERY_MS = 10 * 60_000

export function AccessStaleStrip({ api, onOpenSync }: { api: Api; onOpenSync: () => void }) {
  const [sync, setSync] = useState<AccessSync | null>(null)

  useEffect(() => {
    let cancelled = false
    const read = () => {
      api
        .accessSync()
        .then((d) => !cancelled && setSync(d))
        .catch(() => {})
    }
    const onVisible = () => document.visibilityState === 'visible' && read()
    read()
    const h = setInterval(read, EVERY_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearInterval(h)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [api])

  const words = sync ? staleWords(sync) : null
  if (!words) return null
  return (
    // As the read-grace banner: full-bleed, a bottom rule, the warning tone.
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-(--color-warn)/30 bg-(--color-warn)/8 px-4 py-2.5 text-(length:--text-body) leading-relaxed text-(--color-ink-2)"
    >
      <span aria-hidden className="inline-block h-2 w-2 shrink-0 rounded-full bg-(--color-warn) ring-4 ring-(--color-warn)/15" />
      <span className="font-semibold text-(--color-warn)">{words.headline}</span>
      <span>{words.detail}</span>
      <button
        type="button"
        onClick={onOpenSync}
        className="rounded px-1 font-medium text-(--color-navy-700) hover:underline"
      >
        See Mindzer sync report
      </button>
    </div>
  )
}

/** The loader's outcomes (050) that end an update early, as the strip says them. */
const MISSED: Record<string, string> = {
  HELD: 'stopped at a check',
  REFUSED: 'stopped at a check',
  FETCH_FAILED: 'got no answer from Mindzer',
  ERROR: 'stopped with an error',
}

/** Busy and skipped runs gave way to another run: they are not misses. */
const GAVE_WAY = new Set(['BUSY', 'SKIPPED'])

/** The attempts the API returns (MindzerSyncAdminEndpoints.Attempts). */
const PAGE = 48

/** A run still STARTED after this long never finished: it counts as a miss. */
const RUNNING_MINUTES = 30

/** The strip's two sentences, or null while the copy is within the server's limit. */
function staleWords(d: AccessSync): { headline: string; detail: string } | null {
  const s = d.status
  if (!s) return null
  const old = s.lastGoodUtc === null || (s.minutesSinceGood !== null && s.minutesSinceGood > s.staleAfterMinutes)
  if (!old) return null
  if (s.lastAttemptId === null) {
    return { headline: 'No Mindzer update has run yet.', detail: 'Until one runs, MBI has none of Mindzer’s users.' }
  }

  // The attempts since the last good update (every one when there was none).
  const after = (id: number) => s.lastGoodId === null || id > s.lastGoodId
  const since = d.pulls.filter((p) => after(p.pullId))
  // The newest attempt, still going: not a miss, and said apart.
  const newest = d.pulls[0]
  const running =
    newest?.outcome === 'STARTED' && newest.finishedUtc === null &&
    new Date(s.nowUtc).getTime() - new Date(newest.startedUtc).getTime() < RUNNING_MINUTES * 60_000
  const missed = since.filter((p) => !GAVE_WAY.has(p.outcome) && !(running && p === newest))
  // The list is the newest page only. When all of it came after the good update,
  // the good one lies beyond the page, and so may more misses; with no good
  // update at all, that is so only when the page is full.
  const atLeast = since.length === d.pulls.length && d.pulls.length > 0 && (s.lastGoodId !== null || d.pulls.length >= PAGE)
  const byWords = new Map<string, number>()
  for (const p of missed) {
    const w = MISSED[p.outcome] ?? 'did not finish'
    byWords.set(w, (byWords.get(w) ?? 0) + 1)
  }
  const n = missed.length
  const last = `${atLeast ? 'At least the' : 'The'} last ${n === 1 ? 'update' : `${n} updates`}`
  const why =
    (n === 0
      ? since.length === 0 ? 'No update has run since.' : 'No update has finished since.'
      : byWords.size === 1
        ? `${last} ${[...byWords.keys()][0]}.`
        : `${last} did not finish: ${listWords([...byWords].map(([w, c]) => `${c} ${w}`))}.`) +
    (running ? ' One is running now.' : '')

  if (s.lastGoodUtc === null || s.minutesSinceGood === null) {
    return { headline: 'No Mindzer update has worked yet.', detail: `${why} Until one works, MBI has none of Mindzer’s users.` }
  }
  return {
    headline: `Mindzer data is ${ageWords(s.minutesSinceGood)} old.`,
    detail: `${why} Access still follows the data from ${clock(s.lastGoodUtc)}.`,
  }
}

/** A time today as its hour, any other day with its date. */
function clock(iso: string): string {
  const t = new Date(iso)
  return t.toDateString() === new Date().toDateString()
    ? t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : dayTime(iso)
}
